// Copyright 2021 Sorama B.V.

import { Dimension } from "datatypes/SoramaWebApi";
import { BlobExtensions } from "./BlobExtensions";

export class ImageExtensions {
    /**
     * Generates a thumbnail for the provided image data.
     * @param imageData Either a base64 encoded uri string or a blob representing a valid image (image needs to be supported by the HtmlImageElement).
     * @param maxSize Maximum size in any dimension of the thumbnail.
     */
    public static async generateThumbnail<TImage extends Blob | string>(
        imageData: TImage,
        maxSize: number
    ): Promise<TImage> {
        const imageElement = await ImageExtensions.loadImage(imageData);

        const thumbnailScale = Math.min(maxSize / imageElement.width, maxSize / imageElement.height);
        const canvasElement = document.createElement("canvas");
        canvasElement.width = Math.round(thumbnailScale * imageElement.width);
        canvasElement.height = Math.round(thumbnailScale * imageElement.height);

        const context = canvasElement.getContext("2d");
        context.drawImage(imageElement, 0, 0, canvasElement.width, canvasElement.height);

        return new Promise((resolve) => {
            if (typeof imageData === "string") {
                resolve(<TImage>(<unknown>canvasElement.toDataURL("image/jpg")));
            } else {
                canvasElement.toBlob((blob) => resolve(<TImage>(<unknown>blob)), "image/jpg");
            }
        });
    }

    /**
     * Gets the dimensions of the image in pixels.
     * @param imageData Either a base64 encoded uri string or a blob representing a valid image (image needs to be supported by the HtmlImageElement).
     */
    public static async getSize<TImage extends Blob | string>(imageData: TImage): Promise<Dimension> {
        const imageElement = await ImageExtensions.loadImage(imageData);
        return {
            width: imageElement.width,
            height: imageElement.height,
        };
    }

    private static async loadImage<TImage extends Blob | string>(image: TImage): Promise<HTMLImageElement> {
        const imageElement = new Image();
        const uri =
            typeof image === "string" ? (image as string) : await BlobExtensions.toBase64Uri(<Blob>(<unknown>image));

        return new Promise((resolve, reject) => {
            imageElement.onload = () => {
                resolve(imageElement);
            };

            imageElement.onerror = (e) => {
                reject(e);
            };

            imageElement.src = uri;
        });
    }
}
